import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, Instagram, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

export const Contact = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const interestOptions: string[] = t("contact", "interesOptions");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
    website: "" // honeypot field
  });

  // Keep interest synced with language
  const currentInterest = formData.interest || (Array.isArray(interestOptions) ? interestOptions[0] : "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: { ...formData, interest: currentInterest }
      });

      if (error) throw error;

      toast.success(t("contact", "successToast"));
      setFormData({ name: "", email: "", phone: "", interest: "", message: "", website: "" });
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(t("contact", "errorToast"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">{t("contact", "title")}</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t("contact", "subtitle")}</p>
          <div className="w-24 h-1 bg-gold mx-auto mt-6"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <div>
            <h3 className="text-2xl font-bold mb-6">{t("contact", "infoTitle")}</h3>
            
            <div className="space-y-6 mb-8">
              <a href="tel:+34606649183" className="flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors group">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                  <Phone className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <div className="font-medium text-sm text-muted-foreground">{t("contact", "telefono")}</div>
                  <div className="text-lg font-semibold">+34 606 649 183</div>
                </div>
              </a>

              <a href="mailto:federicoduronio@gmail.com" className="flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors group">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                  <Mail className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <div className="font-medium text-sm text-muted-foreground">{t("contact", "email")}</div>
                  <div className="text-lg font-semibold">federicoduronio@gmail.com</div>
                </div>
              </a>

              <a href="https://instagram.com/federicoduronio" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 rounded-lg hover:bg-muted transition-colors group">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                  <Instagram className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <div className="font-medium text-sm text-muted-foreground">{t("contact", "instagram")}</div>
                  <div className="text-lg font-semibold">@federicoduronio</div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-gold" />
                </div>
                <div>
                  <div className="font-medium text-sm text-muted-foreground">{t("contact", "ubicacion")}</div>
                  <div className="text-lg font-semibold">Málaga, Calle Estonia 5</div>
                </div>
              </div>
            </div>

            <Card className="p-6 bg-muted">
              <h4 className="font-bold mb-3">{t("contact", "horario")}</h4>
              <p className="text-sm text-muted-foreground mb-2">{t("contact", "lunesViernes")}</p>
              <p className="text-sm text-muted-foreground">{t("contact", "sabados")}</p>
            </Card>
          </div>

          <Card className="p-8">
            <h3 className="text-2xl font-bold mb-6">{t("contact", "formTitle")}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users, catches bots */}
              <div className="absolute opacity-0 -z-10" aria-hidden="true" tabIndex={-1}>
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  autoComplete="off"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  tabIndex={-1}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("contact", "nombre")}</label>
                <Input 
                  placeholder={t("contact", "nombrePlaceholder")} 
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("contact", "emailLabel")}</label>
                <Input 
                  type="email" 
                  placeholder="tu@email.com" 
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("contact", "telefonoLabel")}</label>
                <Input 
                  type="tel" 
                  placeholder="+34 600 000 000" 
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("contact", "interes")}</label>
                <select 
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  value={currentInterest}
                  onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                >
                  {Array.isArray(interestOptions) && interestOptions.map((opt: string) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t("contact", "mensaje")}</label>
                <Textarea
                  placeholder={t("contact", "mensajePlaceholder")}
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </div>
              <Button 
                type="submit" 
                variant="premium" 
                className="w-full" 
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? t("contact", "enviando") : t("contact", "enviar")}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};
